import React from 'react'
import { Link } from 'gatsby'

import {
  Layout,
  Header,
  SectionTitle,
  ItemTitle,
  Iconography,
} from '../../../../components'

import './style.scss'
import data from './data.json'

export default () => {
  return (
    <>
      <Header
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
      />
      <Layout
        category="Overview"
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
        complete={true}
      >
        <div className="intro">
          <p>
            Icons are symbols that represent concepts, objects or actions, and
            have semantic purpose within a layout. A useful icon complements a
            text label to communicate a single meaning. Icons should always be
            recognizable, functional and easily understood.
          </p>
        </div>
        <SectionTitle>Attributes</SectionTitle>
        <ItemTitle class="l-margin-top--xlarge">Size</ItemTitle>
        <p>
          Icon size should be consistent throughout the layout to maintain
          visual hierarchy and ensure scannability. Icons should also be
          proportionate to the size of any accompanying text, so keep the pixel
          size of font in mind when pairing icons. Design icons at the 24x24 px
          (small) size and allow resizing in 1.5 or 2x increments to ensure
          smooth scaling and to maintain clarity.
        </p>
        <p>
          Icons may be placed in square or circular frames to grant more
          prominence. To scale frames beyond the defined sizes below, the frame
          should maintain a width 1.33x greater than the icon.
        </p>
        <table class="table">
          <thead>
            <tr>
              <th>Size</th>
              <th>Icon Dimension</th>
              <th>CONTAINER DIMENSION (SQ. OR DIA.)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>XS</td>
              <td>16x16px</td>
              <td>21.33px</td>
            </tr>
            <tr>
              <td>S</td>
              <td>24x24px</td>
              <td>32px</td>
            </tr>
            <tr>
              <td>M</td>
              <td>32x32px</td>
              <td>42.66px</td>
            </tr>
            <tr>
              <td>L</td>
              <td>48x48px</td>
              <td>64px</td>
            </tr>
            <tr>
              <td>XL</td>
              <td>64x64px</td>
              <td>85.33px</td>
            </tr>
          </tbody>
        </table>

        <ItemTitle class="l-margin-top--xxlarge">Clarity</ItemTitle>
        <p>
          Icons should always be placed precisely on-pixel, where the x and y
          coordinates are whole numbers, to avoid distortion.
        </p>

        <ItemTitle class="l-margin-top--xxlarge">Styles</ItemTitle>
        <p>
          <strong>Filled vs Outlined</strong>
        </p>
        <p>
          Wilmington Trust mainly uses outlined icons to achieve a clean and
          simple layout that prioritizes the content on the page. However,
          consider accessibility when choosing a filled vs outlined icon. A
          small filled icon will be more easily perceived than a small outlined
          icon. Additionally, semantic context can change when using a filled or
          outlined version of the same icon. For instance, a filled icon could
          indicate that something has been selected or that an option is "on".
        </p>
        <p>
          The stroke weight of all outline icons should always be 1px and will
          not change as the icon scales. The stroke weight of filled icons will
          scale proportionally as the icon changes size.
        </p>
        <ItemTitle class="l-margin-top--xxlarge">Color</ItemTitle>
        <p>
          Icons in the library are presented in the Primary Base color. Stick to
          Primary or Secondary Base, depending on context, unless otherwise
          necessary to visually reinforce the associated text content, e.g., an
          error icon should use Red Base. Icon color may also need to change to
          ensure sufficient color contrast to meet accessibility standards.
        </p>
        <p>
          Set the color of filled icons by altering the <i>Fill</i> override.
          Set the color of outlines by altering the <i>Stroke</i> override.
        </p>
        <hr />
        <SectionTitle>Accessibility</SectionTitle>
        <p>
          For a seamless experience, include a text label within the page layout
          alongside an icon. Interactive icons should be labeled by the action
          they offer. Illustrative icons should reinforce existing text and
          should not include a label where it will create redundancy. For
          example, repeated icons in a table column can be labeled on-screen by
          that column's header instead of cluttering the design by repeating the
          label in each row.
        </p>
        <p>
          If space constraints make it impossible to include a label on-screen
          and the design cannot be amended to accommodate a label, the Icon
          Tooltip variant of the{' '}
          <Link
            class="link"
            to={'/' + data.site + '/components/tooltip/design'}
          >
            Tooltip
          </Link>{' '}
          may be used. Do not use icons that do not have an easily recognized
          semantic purpose without an on-screen label. A label's placement in
          relation to the icon is up to the designer's discretion but should
          give prominence to any associated actions.
        </p>
        <hr />

        <SectionTitle>Library</SectionTitle>
        <Iconography site={data.site} />
      </Layout>
    </>
  )
}
